import { ChangeDetectorRef, Component, Input, OnInit, Output, TemplateRef, ViewChild, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { Filters } from 'app/model/filters';
import { Roles } from 'app/model/rol';
import { User } from 'app/model/user';
import { CalendarDatatableService } from 'app/shared/services/data/calendar/calendar-datatable.service';
import { FiltersService } from 'app/shared/services/data/filters.service';
import { ScreenService } from 'app/shared/services/data/screen.service';
import { LoginService } from 'app/shared/services/login/login.service';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

@Component({
  selector: 'app-calendar-datatable',
  templateUrl: './calendar-datatable.component.html',
  styleUrls: ['./calendar-datatable.component.scss'],
  providers: [CalendarDatatableService]
})
export class CalendarDatatableComponent implements OnInit, OnDestroy, OnChanges {
  public rowLimit: number
  public employeesCopy: any[]
  public user: User
  public userRoles: string[]
  public days: any[]
  public currentMonth: string
  public currentYear: number
  public isMonthMode: boolean
  public daysInfo: string
  public currentDaysIsLooking: boolean
  public isLeader: boolean
  public isAdmin: boolean
  public ColumnMode = ColumnMode
  public isMobile: boolean
  public holidays: string[]
  public selectedDpto: string | null
  public allRowsIsExpanded: boolean
  public dynamicEmployeesColumnWidth: number
  public leyendFilters: string[]

  @Input() public employees: any[]
  @Input() public leaderRoles: string[]
  @Input() public limitIsShown: boolean = true
  @Input() public dptosAreShown: boolean
  @Input() public dptos: any[]
  @Input() public leyends: any[]
  @Input() public dptoFilter: string
  @Input() public tableMessages: {}
  @Input() public exportButton: TemplateRef<any>
  @Input() public rowDetails: TemplateRef<any>
  @Input() public employeesContent: TemplateRef<any>
  @Input() public tableContent: TemplateRef<any>

  @Output() public holidaysReady: EventEmitter<string[]>
  @Output() public changeTableView: EventEmitter<string[]>
  @Output() public paginationTable: EventEmitter<string[]>

  @ViewChild('tableRowDetails') public tableRowDetails: any

  /**
   * Constructor
   *
   * @param {CalendarDatatableService} calendarDatatableService
   * @param {FiltersService} filtersService
   * @param {ScreenService} screenService
   * @param {LoginService} loginService
   * @param {ChangeDetectorRef} cdr
   * 
   */
  constructor(
    private calendarDatatableService: CalendarDatatableService,
    private filtersService: FiltersService,
    private screenService: ScreenService,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef
  ) {
    this.rowLimit = 10

    this.selectedDpto = null

    this.allRowsIsExpanded = false

    this.currentDaysIsLooking = true

    this.leyendFilters = []

    this.tableMessages = {
      emptyMessage: 'Sin empleados',
      totalMessage: 'En total'
    }

    this.holidaysReady = new EventEmitter<string[]>()

    this.changeTableView = new EventEmitter<string[]>()

    this.paginationTable = new EventEmitter<string[]>()
  }

  /**
   * On init
   */
  ngOnInit() {
    this.screenServiceSuscribe()

    this.getUserFromSessionAndHisRoles()

    this.setHolidays()
  }

  ngOnDestroy(): void {
    this.filtersService.clearFiltersAndText()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employees']) {
      if (!changes['employees'].previousValue && changes['employees'].currentValue)
        this.employeesCopy = [...changes['employees'].currentValue]

      this.calculateEmployeesWidthColum()
    }
  }

  private setDays(daysData: any): void {
    this.days = daysData.days
    this.currentMonth = daysData.currentMonth
    this.currentYear = daysData.currentYear
    this.isMonthMode = daysData.isMonthMode
    this.daysInfo = this.calendarDatatableService.getdaysInfo()
  }

  public activateWeekView(): void {
    const daysData = this.calendarDatatableService.activateWeekView()

    this.setDays(daysData)
    this.checkIfCurrentDayIsLooking()
    this.changeTableView.emit()
  }

  public activateMonthView(): void {
    const daysData = this.calendarDatatableService.activateMonthView()

    this.setDays(daysData)
    this.checkIfCurrentDayIsLooking()
    this.changeTableView.emit()
  }

  public getToday(): void {
    const daysData = this.calendarDatatableService.getToday()

    this.setDays(daysData)
    this.checkIfCurrentDayIsLooking()
    this.paginationTable.emit()
  }

  public getPrevDate(): void {
    const daysData = this.calendarDatatableService.getPrevDate()

    this.setDays(daysData)
    this.checkIfCurrentDayIsLooking()
    this.paginationTable.emit()
  }

  public getNextDate(): void {
    const daysData = this.calendarDatatableService.getNextDate()

    this.setDays(daysData)
    this.checkIfCurrentDayIsLooking()
    this.paginationTable.emit()
  }

  public checkIfCurrentDayIsLooking(): void {
    const now: Date = new Date()
    const dateString: string = this.calendarDatatableService.dateIsoToString(now)

    this.currentDaysIsLooking = this.days.some(item => item.prop === dateString)
  }

  public getLeyendColor(eventStatus: string): string {
    return this.calendarDatatableService.getLeyendColor(eventStatus)
  }

  public rowDetailsToggleExpand(row: any): void {
    this.tableRowDetails.rowDetail.toggleExpandRow(row)
  }

  public allRowsToggleExpand(): void {
    const isExpanded = this.allRowsIsExpanded

    isExpanded
      ? this.tableRowDetails.rowDetail.collapseAllRows()
      : this.tableRowDetails.rowDetail.expandAllRows()

    this.allRowsIsExpanded = !isExpanded
  }

  private screenServiceSuscribe(): void {
    this.screenService.isMobile$.subscribe((data) => {
      this.isMobile = data
    })
  }

  private getUserFromSessionAndHisRoles(): void {
    this.user = this.loginService.getUser()

    this.userRoles = this.user.userRoles.map((item) => item.name)

    this.checkIfUserIsLeader()
  }

  private setHolidays(): void {
    this.calendarDatatableService.setHolidays().subscribe({
      next: (days) => {
        this.setDays(days)

        this.holidays = this.calendarDatatableService.getHolidays()

        this.cdr.detectChanges()

        this.holidaysReady.emit(this.holidays)
      },
      error: () => {
        const alertOptions: SweetAlertOptions = {
          title: '¡Error!',
          html: `Ha ocurrido un error al cargar las vacaciones. <br><br> Póngase en contacto con un administrador.`,
          icon: 'error',
          customClass: {
            confirmButton: 'btn red-bg text-white ml-1'
          },
        }

        this.fireSwalAlert(alertOptions)
      }
    })
  }

  public filterByDpto(): any[] {
    const filters: Filters = { [this.dptoFilter]: this.selectedDpto }

    const filteredEmployees = this.filtersService.filterBy(filters, this.employeesCopy)

    this.employees = [...filteredEmployees]

    this.calculateEmployeesWidthColum()

    return filteredEmployees
  }

  public filterByText(employees: any[]): void {
    this.employees = employees

    this.calculateEmployeesWidthColum()
  }

  public filterByLeyend(event: MouseEvent, type: string): void {
    const leyend: HTMLDivElement = event.target as HTMLDivElement

    const leyendFilters = this.leyendFilters

    const isFiltered = leyendFilters.includes(type)

    isFiltered
      ? this.leyendFilters.splice(leyendFilters.indexOf(type), 1)
      : this.leyendFilters.push(type)

    leyend.classList.toggle('disabled')
  }

  public updateLimit(newLImit: string): void {
    this.rowLimit = parseInt(newLImit)
  }

  private calculateEmployeesWidthColum(): void {
    if (this.employees && this.employees.length > 0 && !this.isMobile)
      this.dynamicEmployeesColumnWidth = this.calendarDatatableService.calculateEmployeesWidthColum(this.employees)
  }

  private checkIfUserIsLeader(): void {
    this.isLeader = this.leaderRoles.some((role) => this.userRoles.includes(role))

    this.isAdmin = this.userRoles.some((role) => role === Roles.ADMIN_ROLE || role === Roles.SUPER_LEADER_ROLE)
  }

  private fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return this.calendarDatatableService.fireSwalAlert(options)
  }
}
